import { getDsTableAdvancedClass } from '../../../../../shared-ui/ds/tableAdvanced.js';
import {
  renderDsStack,
  renderDsTableCell,
  renderDsTableRowMarkup
} from '../../../../../src/ui/index.js';
import { renderSettingsPanel } from './renderSettingsPanel.js';
import { isSettingsSaasFinalUiActive } from './settingsPresentationMode.js';

export function renderSettingsTable(options = {}) {
  const normalized = options && typeof options === 'object' ? options : {};
  const finalUi = isSettingsSaasFinalUiActive();
  const headers = Array.isArray(normalized.headers) && normalized.headers.length
    ? normalized.headers.map((value) => String(value))
    : [''];
  const rows = parseRows(normalizeTableMarkup(String(normalized.bodyHtml || '')));
  const columnCount = Math.max(
    headers.length,
    rows.reduce((max, row) => Math.max(max, row.cells.length), 0),
    1
  );
  const id = String(normalized.id || '').trim();
  const className = String(normalized.className || '').trim();
  const rootClassName = [
    getDsTableAdvancedClass({
      state: 'Default',
      density: 'compact',
      selection: 'none',
      headerVariant: normalized.headerVariant
    }),
    'kg-settings-table',
    finalUi ? `kg-settings-table--columns-${Math.min(columnCount, 16)}` : '',
    className
  ].filter(Boolean).join(' ');
  const styleParts = [`--ds-table-advanced-columns:${String(columnCount)}`];

  const headerHtml = headers.map((header) => renderDsTableCell({
    className: 'ds-table-advanced__cell--header kg-settings-table__cell kg-settings-table__cell--header',
    role: 'columnheader',
    html: escapeHtml(header)
  })).join('');
  const rowsHtml = rows.map((row, index) => {
    const paddedCells = row.cells.slice();
    while (paddedCells.length < columnCount) {
      paddedCells.push({ className: '', style: '', otherAttrs: '', contentHtml: '&nbsp;' });
    }
    const cellHtml = paddedCells.map((cell) => {
      const alignEnd = finalUi && /(?:^|;)\s*text-align\s*:\s*right\s*(?:;|$)/iu.test(cell.style);
      const mergedClass = [
        'ds-table-advanced__cell',
        'ds-table-advanced__cell--body',
        'kg-settings-table__cell',
        alignEnd ? 'kg-settings-table__cell--align-end' : '',
        String(cell.className || '').trim()
      ].filter(Boolean).join(' ');
      const cellMarkup = renderDsTableCell({
        className: mergedClass,
        role: 'cell',
        attrs: {
          ...(!finalUi && cell.style ? { style: cell.style } : {})
        },
        html: cell.contentHtml
      });
      return cell.otherAttrs ? cellMarkup.replace('>', `${cell.otherAttrs}>`) : cellMarkup;
    }).join('');
    const rowTone = index % 2 === 0 ? 'base' : 'alt';
    return renderDsTableRowMarkup({
      className: `kg-settings-table__row kg-settings-table__row--${rowTone}`,
      cellsHtml: cellHtml
    });
  }).join('');

  return renderSettingsPanel({
    label: '',
    size: 'sm',
    className: 'kg-settings-table-panel',
    bodyHtml: renderDsStack({
      component: 'ds/settings-table-wrap',
      className: 'kg-settings-table-wrap',
      html: renderDsStack({
        tag: 'section',
        component: 'ds/table-advanced',
        className: rootClassName,
        attrs: {
          ...(id ? { id } : {}),
          role: 'region',
          ...(finalUi
            ? { 'data-kg-settings-table-columns': String(columnCount) }
            : { style: styleParts.join(';') })
        },
        html: renderDsStack({
          tag: 'div',
          component: 'ds/settings-table-grid',
          className: 'ds-table-advanced__grid kg-settings-table__grid',
          attrs: { role: 'table' },
          html: `${headerHtml}${rowsHtml}`
        })
      })
    })
  });
}

function parseRows(bodyHtml) {
  const rows = [];
  const rowRegex = new RegExp('<' + 'tr\\b[^>]*>([\\s\\S]*?)<\\/tr>', 'gi');
  let rowMatch = rowRegex.exec(bodyHtml);
  while (rowMatch) {
    const rowInner = String(rowMatch[1] || '');
    const cells = [];
    const cellRegex = new RegExp('<' + 't[dh]\\b([^>]*)>([\\s\\S]*?)<\\/t[dh]>', 'gi');
    let cellMatch = cellRegex.exec(rowInner);
    while (cellMatch) {
      const attrsRaw = String(cellMatch[1] || '');
      const contentHtml = String(cellMatch[2] || '').trim() || '&nbsp;';
      const className = getAttributeValue(attrsRaw, 'class');
      const style = getAttributeValue(attrsRaw, 'style');
      const otherAttrs = stripAttribute(stripAttribute(attrsRaw, 'class'), 'style').trim();
      cells.push({
        className,
        style,
        otherAttrs: otherAttrs ? ` ${otherAttrs}` : '',
        contentHtml
      });
      cellMatch = cellRegex.exec(rowInner);
    }
    if (cells.length) rows.push({ cells });
    rowMatch = rowRegex.exec(bodyHtml);
  }
  return rows;
}

function normalizeTableMarkup(value = '') {
  return String(value || '')
    .replace(/\[#tr\b/gi, '<' + 'tr')
    .replace(/\[#td\b/gi, '<' + 'td')
    .replace(/\[#th\b/gi, '<' + 'th');
}

function getAttributeValue(attrs = '', name = '') {
  const pattern = new RegExp(`\\b${escapeRegExp(name)}\\s*=\\s*\"([^\"]*)\"`, 'i');
  const match = String(attrs).match(pattern);
  return match ? String(match[1] || '') : '';
}

function stripAttribute(attrs = '', name = '') {
  const pattern = new RegExp(`\\s*\\b${escapeRegExp(name)}\\s*=\\s*\"[^\"]*\"`, 'ig');
  return String(attrs).replace(pattern, '');
}

function escapeRegExp(value = '') {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
